import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  LinearProgress,
  Alert,
  Paper,
  IconButton,
  Tooltip
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import { toast } from 'react-toastify';
import apiService from '../services/api';
import CRColumnMappingModal from './CRColumnMappingModal';

interface CRDatasetUploadProps {
  onUploadComplete: (s3Key: string) => void;
}

const CRDatasetUpload: React.FC<CRDatasetUploadProps> = ({ onUploadComplete }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'validating' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Column mapping state
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [uploadedS3Key, setUploadedS3Key] = useState<string>('');
  const [availableColumns, setAvailableColumns] = useState<string[]>([]);
  const [missingColumns, setMissingColumns] = useState<string[]>([]);

  const calculateFileHash = async (file: File): Promise<string> => {
    const arrayBuffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Check file type
      const validTypes = ['.csv', '.parquet'];
      const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

      if (!validTypes.includes(fileExtension)) {
        toast.error('Please select a CSV or Parquet file');
        return;
      }

      setSelectedFile(file);
      setUploadStatus('idle');
      setErrorMessage('');
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file first');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadStatus('uploading');
    setErrorMessage('');

    try {
      // Step 1: Calculate file hash
      setUploadProgress(10);
      const fileHash = await calculateFileHash(selectedFile);

      // Step 2: Get presigned upload URL
      setUploadProgress(20);
      const uploadUrlResponse = await apiService.getDatasetUploadUrl(
        selectedFile.name,
        selectedFile.type,
        fileHash
      );

      if (uploadUrlResponse.status === 'duplicate') {
        toast.info('File already exists, using existing dataset');
        // Use existing_parquet_key if available, otherwise fall back to s3_key
        const existingKey = uploadUrlResponse.existing_parquet_key || uploadUrlResponse.s3_key;
        console.log('Using existing dataset:', { existingKey, hasParquet: !!uploadUrlResponse.existing_parquet_key });

        // Validate existing dataset
        await validateDataset(existingKey);
        return;
      }

      // Step 3: Upload file to S3
      setUploadProgress(30);
      console.log('Starting S3 upload...', { url: uploadUrlResponse.upload_url, s3Key: uploadUrlResponse.s3_key });

      const formData = new FormData();

      // Add all fields from presigned post
      Object.keys(uploadUrlResponse.fields).forEach(key => {
        formData.append(key, uploadUrlResponse.fields[key]);
      });

      // File must be the last field
      formData.append('file', selectedFile);

      console.log('FormData prepared, uploading to S3...');

      // Upload to S3 with timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000); // 2 minute timeout

      try {
        const uploadResponse = await fetch(uploadUrlResponse.upload_url, {
          method: 'POST',
          body: formData,
          signal: controller.signal
        });

        clearTimeout(timeoutId);
        console.log('S3 upload response:', { ok: uploadResponse.ok, status: uploadResponse.status, statusText: uploadResponse.statusText });

        if (!uploadResponse.ok) {
          const responseText = await uploadResponse.text();
          console.error('S3 upload failed:', responseText);
          throw new Error(`Upload failed: ${uploadResponse.statusText}`);
        }
      } catch (fetchError: any) {
        clearTimeout(timeoutId);
        if (fetchError.name === 'AbortError') {
          throw new Error('Upload timed out after 2 minutes. Please try a smaller file or check your connection.');
        }
        throw fetchError;
      }

      setUploadProgress(70);

      // Step 4: Notify backend of completion (triggers CSV-to-Parquet conversion)
      const uploadCompleteResponse = await apiService.notifyDatasetUploadComplete(
        uploadUrlResponse.s3_key,
        selectedFile.name,
        fileHash
      );

      console.log('Upload complete response:', uploadCompleteResponse);

      // Step 5: Poll for Parquet conversion completion (event-driven with exponential backoff)
      const parquetKey = uploadCompleteResponse.parquet_key;
      console.log('Polling for Parquet conversion...', { parquetKey });

      setUploadProgress(75);
      setUploadStatus('validating');

      // Poll with exponential backoff
      const maxAttempts = 20; // Max 20 attempts
      let attempt = 0;
      let delay = 500; // Start with 500ms
      let conversionComplete = false;

      while (attempt < maxAttempts && !conversionComplete) {
        attempt++;
        // Snapshot delay into a per-iteration binding so the setTimeout
        // closure doesn't capture the mutating loop variable (no-loop-func).
        const currentDelay = delay;
        await new Promise(resolve => setTimeout(resolve, currentDelay));

        try {
          // Try to validate - if file exists and is valid, this will succeed
          await validateDataset(parquetKey);
          conversionComplete = true;
          console.log(`Parquet conversion completed after ${attempt} attempts (${(attempt * delay / 1000).toFixed(1)}s)`);
        } catch (error: any) {
          const errorMsg = error.message || '';

          // Check if it's a "file not found" error (conversion still in progress)
          if (errorMsg.includes('not found') || errorMsg.includes('does not exist') || errorMsg.includes('magic bytes')) {
            // File doesn't exist yet or conversion in progress - keep polling
            console.log(`Polling attempt ${attempt}/${maxAttempts}, delay: ${delay}ms`);
            setUploadProgress(75 + (attempt / maxAttempts) * 20);

            // Exponential backoff: 500ms, 1s, 2s, then cap at 2s
            delay = Math.min(delay * 2, 2000);
          } else {
            // Different error (validation failure due to schema) - conversion is done
            console.log('Parquet conversion complete but validation failed:', errorMsg);
            throw error;
          }
        }
      }

      if (!conversionComplete) {
        throw new Error('Parquet conversion timed out after 30 seconds. The file may be too large. Please refresh and try again.');
      }

    } catch (error: any) {
      console.error('Upload error:', error);
      setUploadStatus('error');
      setErrorMessage(error.message || 'Upload failed');
      toast.error(error.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const validateDataset = async (s3Key: string) => {
    setUploadStatus('validating');
    setUploadProgress(95);

    try {
      const validationResponse = await apiService.validateCRDataset(s3Key);

      if (validationResponse.valid) {
        // Dataset is valid, notify parent
        setUploadStatus('success');
        setUploadProgress(100);
        toast.success('Dataset uploaded and validated successfully!');
        onUploadComplete(s3Key);
      } else {
        // Dataset needs column mapping
        setUploadedS3Key(s3Key);
        setAvailableColumns(validationResponse.available_columns || []);
        setMissingColumns(validationResponse.missing_columns || []);
        setShowMappingModal(true);
        setUploadStatus('idle');
        toast.warning('Dataset requires column mapping');
      }
    } catch (error: any) {
      setUploadStatus('error');
      setErrorMessage(error.message || 'Validation failed');
      toast.error('Dataset validation failed');
    }
  };

  const handleMappingComplete = (mappedS3Key: string) => {
    setShowMappingModal(false);
    setUploadStatus('success');
    setUploadProgress(100);
    toast.success('Column mapping completed successfully!');
    onUploadComplete(mappedS3Key);
  };

  const getStatusColor = (): 'primary' | 'success' | 'error' => {
    switch (uploadStatus) {
      case 'success':
        return 'success';
      case 'error':
        return 'error';
      default:
        return 'primary';
    }
  };

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 2 }} variant="outlined">
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ flex: 1 }}>
            Upload CR Dataset
          </Typography>
          <Tooltip title="Upload a CSV or Parquet file with CR data. Required columns: ItemGrade, PassageBody, ItemPrompt, StudentResponse, RubricScale, StudentID, Teacher, Item">
            <IconButton size="small">
              <InfoIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}

        <Box sx={{ mb: 2 }}>
          <input
            accept=".csv,.parquet"
            style={{ display: 'none' }}
            id="cr-dataset-upload"
            type="file"
            onChange={handleFileSelect}
            disabled={uploading}
          />
          <label htmlFor="cr-dataset-upload">
            <Button
              variant="outlined"
              component="span"
              startIcon={<CloudUploadIcon />}
              disabled={uploading}
              fullWidth
            >
              Select File
            </Button>
          </label>
        </Box>

        {selectedFile && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Selected: <strong>{selectedFile.name}</strong> (
              {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB)
            </Typography>
          </Box>
        )}

        {(uploadStatus === 'uploading' || uploadStatus === 'validating') && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2" sx={{ flex: 1 }}>
                {uploadStatus === 'uploading' ? 'Uploading...' : 'Validating...'}
              </Typography>
              <Typography variant="body2">{uploadProgress}%</Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={uploadProgress}
              color={getStatusColor()}
            />
          </Box>
        )}

        {uploadStatus === 'success' && (
          <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
            Dataset uploaded and validated successfully!
          </Alert>
        )}

        <Button
          variant="contained"
          color="primary"
          onClick={handleUpload}
          disabled={!selectedFile || uploading || uploadStatus === 'success'}
          startIcon={uploading ? null : <CloudUploadIcon />}
          fullWidth
        >
          {uploading ? 'Uploading...' : 'Upload Dataset'}
        </Button>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Supported formats: CSV, Parquet
        </Typography>
      </Paper>

      {/* Column Mapping Modal */}
      <CRColumnMappingModal
        open={showMappingModal}
        onClose={() => {
          setShowMappingModal(false);
          setUploadStatus('idle');
        }}
        s3Key={uploadedS3Key}
        availableColumns={availableColumns}
        missingColumns={missingColumns}
        onMappingComplete={handleMappingComplete}
      />
    </Box>
  );
};

export default CRDatasetUpload;
